import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Baby, Scales, X, HeartStraight, Timer } from "@phosphor-icons/react";
import { calcPregnancy, trimesterLabel, remainingToHPL, calcBMI, formatDateLong } from "@/lib/constants";

const inputCls =
  "w-full rounded-xl border border-pink-100 bg-[#FDFBFE] px-4 py-3 text-sm text-slate-800 outline-none transition-colors placeholder:text-slate-400 focus:border-[#EC4899] focus:bg-white focus:ring-2 focus:ring-[#EC4899]/15";

function Modal({ open, onClose, title, icon, children, testid }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4" onClick={onClose}>
      <div
        data-testid={testid}
        onClick={(e) => e.stopPropagation()}
        className="animate-fade-up max-h-[92vh] w-full overflow-y-auto rounded-t-3xl bg-white p-6 shadow-2xl sm:max-w-md sm:rounded-3xl"
      >
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#FCE7F3] text-[#EC4899]">{icon}</span>
            <h3 className="font-heading text-lg font-extrabold text-[#1E1B4B]">{title}</h3>
          </div>
          <button data-testid="calc-close" onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={22} weight="bold" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function ResultRow({ label, value, strong }) {
  return (
    <div className="flex items-center justify-between gap-3 border-b border-pink-50 py-2 last:border-0">
      <span className="text-sm text-slate-500">{label}</span>
      <span className={`text-right text-sm ${strong ? "font-extrabold text-[#DB2777]" : "font-semibold text-slate-800"}`}>{value}</span>
    </div>
  );
}

function PregnancyCalc() {
  const [hpht, setHpht] = useState("");
  const preg = useMemo(() => calcPregnancy(hpht), [hpht]);
  const rem = useMemo(() => remainingToHPL(hpht), [hpht]);
  return (
    <div className="space-y-4">
      <div>
        <label className="mb-1.5 block text-xs font-bold text-slate-500">Hari Pertama Haid Terakhir (HPHT)</label>
        <input type="date" data-testid="calc-hpht" value={hpht} onChange={(e) => setHpht(e.target.value)} className={inputCls} />
      </div>
      {preg && (
        <div data-testid="calc-hamil-result" className="rounded-2xl border border-pink-100 bg-[#FFF5F9] p-4">
          <ResultRow label="Usia Kehamilan Saat Ini" value={preg.ukText} strong />
          <ResultRow label="Hari Perkiraan Lahir (HPL)" value={formatDateLong(preg.hpl)} strong />
          <ResultRow label="Trimester" value={trimesterLabel(preg.weeks)} />
          {rem && <ResultRow label="Perkiraan Sisa Menuju HPL" value={rem.text} />}
        </div>
      )}
      <p className="rounded-xl bg-slate-50 px-3 py-2 text-[11px] leading-relaxed text-slate-500">
        Perhitungan ini merupakan estimasi berdasarkan HPHT. Penentuan usia kehamilan dan HPL tetap perlu dikonfirmasi oleh tenaga kesehatan, terutama bila siklus menstruasi tidak teratur.
      </p>
    </div>
  );
}

function BMICalc() {
  const [tinggi, setTinggi] = useState("");
  const [berat, setBerat] = useState("");
  const [hamil, setHamil] = useState(false);
  const res = useMemo(() => calcBMI(tinggi, berat), [tinggi, berat]);
  return (
    <div className="space-y-4">
      <div>
        <label className="mb-1.5 block text-xs font-bold text-slate-500">Apakah sedang hamil?</label>
        <div className="grid grid-cols-2 gap-2">
          <button data-testid="calc-bmi-notpregnant" onClick={() => setHamil(false)} className={`rounded-xl py-2.5 text-sm font-bold transition-colors ${!hamil ? "bg-[#EC4899] text-white" : "bg-pink-50 text-[#DB2777]"}`}>Tidak</button>
          <button data-testid="calc-bmi-pregnant" onClick={() => setHamil(true)} className={`rounded-xl py-2.5 text-sm font-bold transition-colors ${hamil ? "bg-[#EC4899] text-white" : "bg-pink-50 text-[#DB2777]"}`}>Ya</button>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="mb-1.5 block text-xs font-bold text-slate-500">Tinggi Badan (cm)</label>
          <input type="number" data-testid="calc-tinggi" value={tinggi} onChange={(e) => setTinggi(e.target.value)} placeholder="cth. 160" className={inputCls} />
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-bold text-slate-500">{hamil ? "Berat sebelum hamil (kg)" : "Berat Badan (kg)"}</label>
          <input type="number" data-testid="calc-berat" value={berat} onChange={(e) => setBerat(e.target.value)} placeholder="cth. 55" className={inputCls} />
        </div>
      </div>
      {res && (
        <div data-testid="calc-bmi-result" className="rounded-2xl border border-pink-100 bg-[#FFF5F9] p-4">
          <ResultRow label={hamil ? "BMI sebelum hamil" : "BMI Anda"} value={String(res.bmi).replace(".", ",")} strong />
          <ResultRow label="Kategori" value={res.cat} />
          {hamil && (
            <p className="mt-3 rounded-xl bg-white px-3 py-2 text-xs leading-relaxed text-slate-600">
              Pada kehamilan tunggal, rekomendasi kenaikan berat badan total selama kehamilan sekitar <span className="font-bold text-[#DB2777]">{res.rec}</span>.
            </p>
          )}
        </div>
      )}
      <p className="rounded-xl bg-slate-50 px-3 py-2 text-[11px] leading-relaxed text-slate-500">
        {hamil
          ? "Rekomendasi ini bersifat umum dan menggunakan BMI sebelum hamil. Target kenaikan berat badan dapat berbeda sesuai kondisi ibu, jumlah janin, penyakit penyerta, dan penilaian tenaga kesehatan."
          : "Hasil BMI bersifat edukasi dan bukan diagnosis medis. Konsultasikan dengan tenaga kesehatan untuk penilaian lebih lanjut."}
      </p>
    </div>
  );
}

export default function HealthCalculators() {
  const [open, setOpen] = useState(null); // 'hamil' | 'bmi' | null
  const Card = ({ id, icon, title, desc, testid }) => (
    <button
      data-testid={testid}
      onClick={() => setOpen(id)}
      className="group flex flex-1 flex-col items-start gap-2 rounded-3xl border border-pink-100 bg-white p-5 text-left shadow-[0_10px_28px_-16px_rgba(236,72,153,0.5)] transition-transform duration-200 hover:-translate-y-0.5 active:scale-[0.99]"
    >
      <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-[#FCE7F3] to-[#F9D6E8] text-[#EC4899]">{icon}</span>
      <span className="font-heading text-base font-extrabold text-[#1E1B4B]">{title}</span>
      <span className="text-xs leading-snug text-slate-500">{desc}</span>
    </button>
  );

  return (
    <div data-testid="health-calculators" className="space-y-3">
      <div className="flex items-center justify-center gap-2">
        <HeartStraight size={16} weight="fill" className="text-pink-300" />
        <h2 className="text-center font-heading text-sm font-extrabold uppercase tracking-widest text-[#DB2777]">Kalkulator Kesehatan GBS</h2>
        <HeartStraight size={16} weight="fill" className="text-pink-300" />
      </div>
      <div className="flex flex-col gap-3 sm:flex-row">
        <Card id="hamil" testid="calc-card-hamil" icon={<Baby size={26} weight="duotone" />} title="Usia Kehamilan & HPL" desc="Hitung usia kehamilan dan perkiraan tanggal lahir." />
        <Card id="bmi" testid="calc-card-bmi" icon={<Scales size={26} weight="duotone" />} title="BMI & Berat Badan Kehamilan" desc="Hitung BMI dan rekomendasi kenaikan berat badan selama kehamilan." />
        <Link
          to="/timer-kontraksi"
          data-testid="calc-card-timer"
          className="group flex flex-1 flex-col items-start gap-2 rounded-3xl border border-pink-100 bg-white p-5 text-left shadow-[0_10px_28px_-16px_rgba(236,72,153,0.5)] transition-transform duration-200 hover:-translate-y-0.5 active:scale-[0.99]"
        >
          <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-[#EEE6FF] to-[#F9D6E8] text-[#7C4DE0]"><Timer size={26} weight="duotone" /></span>
          <span className="font-heading text-base font-extrabold text-[#1E1B4B]">Timer Kontraksi</span>
          <span className="text-xs leading-snug text-slate-500">Catat durasi & jarak kontraksi menjelang persalinan.</span>
        </Link>
      </div>

      <Modal open={open === "hamil"} onClose={() => setOpen(null)} title="Usia Kehamilan & HPL" icon={<Baby size={22} weight="duotone" />} testid="calc-modal-hamil">
        <PregnancyCalc />
      </Modal>
      <Modal open={open === "bmi"} onClose={() => setOpen(null)} title="Hitung BMI" icon={<Scales size={22} weight="duotone" />} testid="calc-modal-bmi">
        <BMICalc />
      </Modal>
    </div>
  );
}
